import { useTranslation } from '@pancakeswap/localization'
import { Button, Flex, Heading, TooltipText, useToast, useTooltip, useModal, Balance, Text } from '@pancakeswap/uikit'
import { FarmWidget } from '@pancakeswap/widgets-internal'
import { useAccount } from 'wagmi'
import { SendTransactionResult } from 'wagmi/actions'
import BigNumber from 'bignumber.js'
import { ToastDescriptionWithTx } from 'components/Toast'
import useCatchTxError from 'hooks/useCatchTxError'
import { styled } from 'styled-components'

import { useCakePrice } from 'hooks/useCakePrice'
import { BIG_ZERO } from '@pancakeswap/utils/bigNumber'
import { getBalanceAmount } from '@pancakeswap/utils/formatBalance'
import { Token } from '@pancakeswap/sdk'
import MultiChainHarvestModal from 'views/Farms/components/MultiChainHarvestModal'
import { useRouter } from 'next/router'

const StyledButton = styled(Button)`
  font-family: inherit;
  font-size: 16px;
  border-radius: 24px;
  padding: 12px 24px;
  height: fit-content;
  margin-top: 6px;
`

interface FarmCardActionsProps {
  pid?: number
  token?: Token
  quoteToken?: Token
  earnings?: BigNumber
  vaultPid?: number
  proxyCakeBalance?: number
  lpSymbol?: string
  onReward?: () => Promise<SendTransactionResult>
  onDone?: () => void
}

const HarvestAction: React.FC<React.PropsWithChildren<FarmCardActionsProps>> = ({
  pid,
  token,
  quoteToken,
  vaultPid,
  earnings,
  proxyCakeBalance,
  lpSymbol,
  onReward,
  onDone,
}) => {
  const { pathname } = useRouter()
  const { address: account } = useAccount()
  // const { toastSuccess } = useToast()
  const { fetchWithCatchTxError, loading: pendingTx } = useCatchTxError()
  const { t } = useTranslation()
  const cakePrice = useCakePrice()
  const isPRC20 = pathname.includes('prc20farms')

  const rawEarningsBalance = account ? getBalanceAmount(earnings) : BIG_ZERO
  const displayBalance = rawEarningsBalance.toFixed(5, BigNumber.ROUND_DOWN)
  const earningsBusd = rawEarningsBalance ? rawEarningsBalance.multipliedBy(cakePrice).toNumber() : 0
  const tooltipBalance = rawEarningsBalance.isGreaterThan(FarmWidget.FARMS_SMALL_AMOUNT_THRESHOLD)
    ? displayBalance
    : '< 0.00001'
  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    `${tooltipBalance} ${t(
      `BBC has been harvested to the farm booster contract and will be automatically sent to your wallet upon the next harvest.`,
    )}`,
    {
      placement: 'bottom',
    },
  )

  const onClickHarvestButton = () => {
    if (vaultPid) {
      onPresentNonBscHarvestModal()
    } else {
      handleHarvest()
    }
  }

  const handleHarvest = async () => {
    const receipt = await fetchWithCatchTxError(() => {
      return onReward()
    }, {
      type: 'harvest-farm',
      summary: `Rewarded ${displayBalance} BBC from ${isPRC20 ? "PRC20" : ""} farm`,
      translatableSummary: {
        text: `Rewarded %amount% from ${isPRC20 ? "PRC20" : ""} farm`,
        data: {
          amount: displayBalance 
        }
      }
    })
    if (receipt?.status) {
      // toastSuccess(
      //   `${t('Harvested')}!`,
      //   <ToastDescriptionWithTx txHash={receipt.transactionHash}>
      //     {t('Your %symbol% earnings have been sent to your wallet!', { symbol: 'BBC' })}
      //   </ToastDescriptionWithTx>,
      // )
      onDone?.()
    }
  }

  const [onPresentNonBscHarvestModal] = useModal(
    <MultiChainHarvestModal
      pid={pid}
      token={token}
      lpSymbol={lpSymbol}
      quoteToken={quoteToken}
      earningsBigNumber={earnings}
      earningsBusd={earningsBusd}
    />,
  )

  return (
    <Flex mb="8px" justifyContent="space-between">
      <Flex flexDirection="column" alignItems="flex-start">
        <Text color="textSubtle" fontSize="16px">
          {t('BBC Earned')}
        </Text>
        {proxyCakeBalance ? (
          <>
            <TooltipText ref={targetRef} decorationColor="secondary">
              <Heading color={rawEarningsBalance.eq(0) ? 'textDisabled' : 'text'}>{rawEarningsBalance.eq(0) ? '-' : displayBalance}</Heading>
            </TooltipText>
            {tooltipVisible && tooltip}
          </>
        ) : (
          <Heading fontSize={20} color={rawEarningsBalance.eq(0) ? 'textDisabled' : 'text'}>{rawEarningsBalance.eq(0) ? '-' : displayBalance}</Heading>
        )}
        <Balance fontSize="12px" color="textSubtle" decimals={2} value={earningsBusd} unit=" USD" prefix={rawEarningsBalance.eq(0) ? '' : "~"} />
      </Flex>
      <StyledButton disabled={rawEarningsBalance.eq(0) || pendingTx} onClick={onClickHarvestButton}>
        {pendingTx ? t('Harvesting') : t('Harvest')}
      </StyledButton>
    </Flex>
  )
}

export default HarvestAction
